/**
 * CSV export for the triage list.
 *
 * Exports what is on screen: the rows the user selected, with the columns the list shows. Exporting
 * every row matching the current filter would need a server round trip, since the client only ever
 * holds one page.
 */

import {
    SreEntityType,
    SreInflightItem,
    SreValidationFindingRow,
} from '../../../../../AGSS/models/SreHealth';
import { formatFindingEntry, uniqueFindingEntries } from './findingFormat';
import { formatUtcDateTime } from './formatting';
import { downloadCsv as downloadCsvContent } from '../../../../../Common/util/downloadCsv';

interface CsvColumn {
    header: string;
    value: (item: SreInflightItem) => string;
}

/**
 * Spreadsheets treat a leading =, +, - or @ as the start of a formula, so a crafted identifier could
 * execute on open. Prefixing with a single quote keeps the text intact and inert.
 */
const neutralizeFormula = (value: string): string =>
    /^[\s]*[=+\-@]|^[\t\r\n]/.test(value) ? `'${value}` : value;

const escapeCell = (value: string): string => {
    const safe = neutralizeFormula(value ?? '');
    return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

const text = (value: string | null | undefined): string => value ?? '';

const buildColumns = (type: SreEntityType): CsvColumn[] => {
    const shared: CsvColumn[] = [
        { header: 'Orchestration row ID', value: (item) => String(item.id) },
        { header: 'Unique identifier', value: (i) => text(i.uniqueIdentifier) },
        {
            header: 'Stage',
            value: (i) =>
                i.stageNumber >= 0
                    ? `${i.stageNumber} · ${i.stage}`
                    : text(i.stage),
        },
        {
            header: 'Step',
            value: (i) =>
                i.stageOrder ? `${i.stageOrder} of ${i.stageCount}` : '',
        },
        { header: 'Stage state', value: (i) => text(i.stageState) },
        {
            header: 'Orchestration type',
            value: (i) => text(i.orchestrationType),
        },
        { header: 'Retry count', value: (i) => String(i.retryCount ?? 0) },
        {
            header: 'Orchestration count',
            value: (i) => String(i.orchestrationCount ?? 0),
        },
        {
            header: 'Latest completed stage',
            value: (i) => text(i.latestCompletedStage),
        },
        {
            header: 'Latest status message',
            value: (i) => text(i.latestStatusMessage),
        },
        {
            header: 'Written time (UTC)',
            value: (i) => formatUtcDateTime(i.writtenTime),
        },
        {
            header: 'Last modified (UTC)',
            value: (i) => formatUtcDateTime(i.lastModifiedTime),
        },
        { header: 'IcM id', value: (i) => (i.icmId ? String(i.icmId) : '') },
    ];

    if (type === 'build') {
        return [
            ...shared,
            {
                header: 'Staged in lowshare (UTC)',
                value: (i) => formatUtcDateTime(i.stagedInLowshare),
            },
            { header: 'Pipeline', value: (i) => text(i.pipeline) },
            { header: 'Region', value: (i) => text(i.region) },
            { header: 'Artifact ID', value: (i) => text(i.artifactID) },
        ];
    }

    return [
        ...shared,
        { header: 'Deployment type', value: (i) => text(i.deploymentType) },
    ];
};

export const buildInflightCsv = (
    type: SreEntityType,
    items: SreInflightItem[]
): string => {
    const columns = buildColumns(type);
    const rows = items.map((item) =>
        columns.map((column) => escapeCell(column.value(item))).join(',')
    );

    return [columns.map((c) => escapeCell(c.header)).join(','), ...rows].join(
        '\r\n'
    );
};

export const inflightCsvFileName = (type: SreEntityType): string => {
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    return `sre-triage-${type}-${stamp}.csv`;
};

export const findingsCsvFileName = (type: SreEntityType): string => {
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    return `sre-findings-${type}-${stamp}.csv`;
};

/**
 * One row per offending file or hash, across every orchestration matching the filter.
 *
 * `Entry` holds a file path or Build/<sha256> depending on the stage, so `Is hash` is there to make
 * a spreadsheet filterable rather than leaving people to eyeball which is which.
 */
export const buildFindingsCsv = (rows: SreValidationFindingRow[]): string => {
    const columns: Array<{
        header: string;
        value: (row: SreValidationFindingRow) => string;
    }> = [
        { header: 'Unique identifier', value: (r) => r.uniqueIdentifier },
        { header: 'Stage', value: (r) => `${r.stageNumber} · ${r.stage}` },
        { header: 'Category', value: (r) => r.category },
        { header: 'Entry', value: (row) => formatFindingEntry(row.entry) },
        { header: 'Is hash', value: (r) => (r.isHash ? 'yes' : 'no') },
        { header: 'Reason', value: (r) => r.reason },
        { header: 'Pipeline', value: (r) => r.pipeline },
        { header: 'Region', value: (r) => r.region },
        { header: 'Artifact ID', value: (r) => r.artifactID },
        { header: 'Orchestration type', value: (r) => r.orchestrationType },
    ];

    const body = rows.map((row) =>
        columns.map((column) => escapeCell(column.value(row))).join(',')
    );

    return [columns.map((c) => escapeCell(c.header)).join(','), ...body].join(
        '\r\n'
    );
};

export const buildUniqueFindingsCsv = (entries: readonly string[]): string =>
    ['Entry', ...uniqueFindingEntries(entries).map(escapeCell)].join('\r\n');

export const downloadCsv = (fileName: string, csv: string): void => {
    // The BOM is what makes Excel read the file as UTF-8 rather than the local ANSI code page.
    downloadCsvContent(`\uFEFF${csv}`, fileName);
};
